import type { Placement } from '@floating-ui/react';
import type { ReactNode } from 'react';
import type {
  NetworkGraphEdgeModel,
  NetworkGraphNodeModel,
} from './NetworkGraph';
import styles from './NetworkGraph.module.scss';
import type { NetworkGraphPosition } from './utils/computeRadialLayout';
import { edgeLabelBox } from './utils/networkGraphEdgeUtils';
import { nodeOuterRadius } from './utils/nodeGeometry';
import { tooltipPlacementAwayFromRay } from './utils/tooltipPlacementAwayFromRay';

// Gap between the tooltip anchor and the node edge / edge label it clears.
const TOOLTIP_GAP = 4;

export interface EdgeTooltipLayerProps {
  edgeId: string | null;
  // Set when the tooltip is surfaced by hovering a node (not the edge itself):
  // its bubble is anchored to this node so it never covers either of the two
  // nodes on the connecting edge. Placement opens away from the node's label
  // ray (see rayAngles) so the bubble does not sit on top of the name.
  anchorNodeId: string | null;
  edges: readonly NetworkGraphEdgeModel[];
  positions: Readonly<Record<string, NetworkGraphPosition>>;
  nodeById: Map<string, NetworkGraphNodeModel>;
  rayAngles: Readonly<Record<string, number>>;
  targetAngleOffsets: Map<string, number>;
  laneOffsets: Map<string, number>;
  renderEdgeTooltip?: (content: ReactNode, placement: Placement) => ReactNode;
}

export const EdgeTooltipLayer = ({
  edgeId,
  anchorNodeId,
  edges,
  positions,
  nodeById,
  rayAngles,
  targetAngleOffsets,
  laneOffsets,
  renderEdgeTooltip,
}: EdgeTooltipLayerProps) => {
  const edge = edgeId ? edges.find(item => item.id === edgeId) : undefined;
  // No built-in bubble: a tooltip only renders when the consumer supplies one.
  if (!edge?.tooltip || !renderEdgeTooltip) {
    return null;
  }

  const source = positions[edge.sourceId];
  const target = positions[edge.targetId];
  const sourceNode = nodeById.get(edge.sourceId);
  const targetNode = nodeById.get(edge.targetId);
  if (!source || !target || !sourceNode || !targetNode) {
    return null;
  }

  const tooltipLabelBox = edgeLabelBox({
    edge,
    source: sourceNode,
    sourcePosition: source,
    target: targetNode,
    targetPosition: target,
    targetAngleOffset: targetAngleOffsets.get(edge.id),
    lateralOffset: laneOffsets.get(edge.id),
  });
  // Use the full bounds of the hovered shape as Floating UI's reference.
  // Point references drift off-center for top placement and cannot clear the
  // opposite edge when Floating UI flips a right-placed tooltip to the left.
  const hoveredNode =
    anchorNodeId === edge.sourceId
      ? { node: sourceNode, position: source }
      : anchorNodeId === edge.targetId
        ? { node: targetNode, position: target }
        : null;
  const nodeReferenceRadius = hoveredNode
    ? nodeOuterRadius(hoveredNode.node.size) + TOOLTIP_GAP
    : 0;
  const rayAngle = hoveredNode ? rayAngles[hoveredNode.node.id] : undefined;
  // Active labels are pinned below the node — keep the historical 'top'
  // opening. Otherwise open opposite the label ray when the layout recorded one.
  const nodePlacement: Placement =
    hoveredNode?.node.state === 'active' || rayAngle === undefined
      ? 'top'
      : tooltipPlacementAwayFromRay(rayAngle);
  const reference = hoveredNode
    ? {
        x: hoveredNode.position.x - nodeReferenceRadius,
        y: hoveredNode.position.y - nodeReferenceRadius,
        width: nodeReferenceRadius * 2,
        height: nodeReferenceRadius * 2,
        placement: nodePlacement,
      }
    : {
        x: tooltipLabelBox.x - tooltipLabelBox.width / 2 - TOOLTIP_GAP,
        y: tooltipLabelBox.y - tooltipLabelBox.height / 2 - TOOLTIP_GAP,
        width: tooltipLabelBox.width + TOOLTIP_GAP * 2,
        height: tooltipLabelBox.height + TOOLTIP_GAP * 2,
        placement: 'right' as Placement,
      };

  return (
    <foreignObject
      className={styles.edgeTooltipObject}
      height={reference.height}
      width={reference.width}
      x={reference.x}
      y={reference.y}
    >
      {renderEdgeTooltip(edge.tooltip, reference.placement)}
    </foreignObject>
  );
};
